import { XMarkIcon } from "@heroicons/react/24/outline";
import moment from "moment-timezone";
import { Button, Text, TextInput } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";

const EVENT_TIMEZONE = "America/New_York";
const INPUT_FORMAT = "YYYY-MM-DDTHH:mm";
// Browsers emit seconds in datetime-local values in some locales.
const ACCEPTED_FORMATS = [INPUT_FORMAT, `${INPUT_FORMAT}:ss`];
const DISPLAY_FORMAT = "ddd, MMM D YYYY, h:mm A";

interface MockTimeControlProps {
  now: moment.Moment;
  mockNow: moment.Moment | null;
  setMockNow: (mockNow: moment.Moment | null) => void;
}

const MockTimeControl: React.FC<MockTimeControlProps> = ({
  now,
  mockNow,
  setMockNow,
}) => {
  const [draft, setDraft] = useState(() => now.format(INPUT_FORMAT));
  const [isDismissed, setIsDismissed] = useState(false);

  const parsedDraft = moment.tz(draft, ACCEPTED_FORMATS, EVENT_TIMEZONE);
  const isDraftValid = draft !== "" && parsedDraft.isValid();

  if (isDismissed) return null;

  return (
    <Wrapper>
      <Blurb>
        <Text type="body2">
          <strong>Dev tool:</strong> pretend it is a different time (Eastern).
          Currently using <strong>{now.format(DISPLAY_FORMAT)}</strong>
          {mockNow ? " (mocked)" : " (live)"}.
        </Text>
        <DismissButton
          type="button"
          aria-label="Hide dev tool"
          title="Hide dev tool and go back to live time (refresh to bring it back)"
          onClick={() => {
            // Leaving a mock time running behind a hidden panel is how you end
            // up debugging shift states that are not really wrong.
            setMockNow(null);
            setIsDismissed(true);
          }}
        >
          <XMarkIcon width="16px" height="16px" />
        </DismissButton>
      </Blurb>
      <Controls>
        <TimeInput
          size="sm"
          type="datetime-local"
          value={draft}
          error={
            draft !== "" && !isDraftValid
              ? "Not a valid date and time"
              : undefined
          }
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(e.target.value)
          }
        />
        <Button
          size="sm"
          color="secondary"
          disabled={!isDraftValid}
          onClick={() => setMockNow(parsedDraft)}
        >
          Use mock time
        </Button>
        <Button
          size="sm"
          color="secondary"
          disabled={!mockNow}
          onClick={() => {
            setMockNow(null);
            setDraft(moment().tz(EVENT_TIMEZONE).format(INPUT_FORMAT));
          }}
        >
          Reset to live
        </Button>
      </Controls>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  border: 1px dashed var(--North-Border-Gray, #d1d5db);
  border-radius: 8px;
`;

const Blurb = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  display: flex;
  padding: 2px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};

  &:hover {
    color: ${({ theme }) => theme.globalConstants.color.textBody};
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const TimeInput = styled(TextInput)`
  max-width: 240px;
`;

export default MockTimeControl;
